import React from 'react';
import Button from './Button';

/**
 * Catches a render error below it and shows it in place, so one broken panel
 * does not blank the whole page. Give it a `key` that changes when the content
 * changes (the open section, say) to clear the error.
 */
export interface ErrorBoundaryProps {
  /** What failed, for the message: "the Backlinks section". */
  label?: string;
  children?: React.ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`Render error in ${this.props.label ?? 'a panel'}:`, error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          padding: 'var(--space-5)',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ fontWeight: 'var(--weight-medium)', color: 'var(--status-down)' }}>
          Something went wrong in {this.props.label ?? 'this panel'}.
        </div>
        <code style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
          {error.message}
        </code>
        <Button size="sm" variant="secondary" onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </div>
    );
  }
}
